import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import { WorkspaceRole } from '../../../__legacy__/modules/workspaces/workspaces.store';
import { Home } from '../../../__legacy__/routes/config/Home';
import { IOverlayLoaderStore } from '../../common/OverlayLoader.store';
import { useJobPermissions } from '../../jobs/jobPermissions';
import { Settings } from '../../settings/SettingsContext';
import { ICurrentWorkspaceStore } from '../../workspaces/currentWorkspace/CurrentWorkspace.store';
import { useCloudCostsService } from '../cloudCosts.service';
import { CloudCostsHistory } from '../components/CloudCostsHistory';
import { CloudCostsTabsContainer } from './CloudCostsTabs.container';

export const CloudCostsHistoryContainer = observer((): ReactElement => {
  const cloudCostsService = useCloudCostsService();
  const overlayLoader = useInjection(IOverlayLoaderStore.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const history = useHistory();
  const permissions = useJobPermissions();

  const settingsContext = useContext(Settings);

  const refreshAsync = async (): Promise<void> => {
    const promises = [cloudCostsService.refreshAsync()];

    if (permissions.canReadCostList(false).allowed) {
      promises.push(cloudCostsService.getUserEmailsAsync());
    }

    await Promise.all(promises);
  };

  const checkAccess = (): boolean => {
    const canReadCostsList = permissions.canReadCostList(true).allowed && !currentWorkspaceStore.currentWorkspace.encryption.encrypted;

    if (!canReadCostsList) {
      history.push(Home.Projects.List.All.withParams({ workspaceId: currentWorkspaceStore.currentWorkspace.id }));
      return false;
    }

    return true;
  };

  useEffect((): void => {
    if (!checkAccess()) {
      return;
    }
    void refreshAsync();
  }, [currentWorkspaceStore.currentWorkspace.id]);

  const handlePaginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    await cloudCostsService.changePagination(pageNumber, pageSize);
  };

  const handleChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    settingsContext.setCloudCostsHistoryPageSize(pageSize);
    await handlePaginationChange(pageNumber, pageSize);
  };

  const handleChangeUser = async (value: string): Promise<void> => {
    await cloudCostsService.handleChangeSelectedUser(value);
  };

  const handleOrderChange = async (orderBy: string, orderType: string): Promise<void> => {
    await cloudCostsService.changeOrder(orderBy, orderType || 'asc');
  };

  const { historyData, historyDataPaging, chartData, userId, usersList } = cloudCostsService.store;
  const { pageNumber, totalCount, orderType, orderBy } = historyDataPaging;

  return (
    <div>
      <CloudCostsTabsContainer />
      <CloudCostsHistory
        isLoading={overlayLoader.isSpinnerVisible('cloud-costs-list')}
        workspaceId={currentWorkspaceStore.currentWorkspace.id}
        historyData={historyData}
        users={usersList}
        userId={userId}
        isSelectDisabled={currentWorkspaceStore.currentWorkspace.role === WorkspaceRole.Trainer}
        pagination={{
          pageNumber,
          totalCount,
          pageSize: settingsContext.store.cloudCostsHistoryPageSize,
          onChange: handleChange,
        }}
        sorting={{
          orderBy: orderBy || '',
          orderType: orderType || '',
        }}
        onSortingChange={handleOrderChange}
        chartData={chartData}
        handleChangeUser={handleChangeUser}
      />
    </div>
  );
});
